import { useCallback, useEffect } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Popup } from "components/popups/Popup";
import Button from "components/shared/button";
import Input from "components/shared/inputs/input";
import Loader from "components/shared/loader";
import { SvgSupprimer } from "components/svg";

import useCustomForm from "utils/hooks/useCustomForm";
import useTranslation from "utils/hooks/useTranslation";
import { useCreateClientMutation, useDeleteClientMutation, useUpdateClientMutation } from "store/api";

//import { DeleteClientPopup } from "./DeleteClientPopup";
import { usePopup } from "providers/PopupProvider";
import { useDispatch } from "react-redux";
import { addNotification } from "store/componentsSlice";
import { ConfirmationPopup } from "./ConfirmationPopup";
import { useAppSelector } from "store/hooks";

interface ClientFormData {
  name: string;
  phone?: string | null;
  address: string;
  city: string;
  companyName: string;
  cp: string;
}

export const ClientPopup: React.FC<{
  closePopup: () => void;
  client?: Client; // For edit mode
}> = ({ closePopup, client }) => {
  const { t } = useTranslation();
  const authState = useAppSelector((state) => state.auth);
  const [createClient, { isLoading: isCreating }] = useCreateClientMutation();
  const [updateClient, { isLoading: isUpdating }] = useUpdateClientMutation();
  const [deleteClient, { isLoading: isDeleting }] = useDeleteClientMutation();

  const { openPopup, closeLastPopup } = usePopup();
  const dispatch = useDispatch();

  const isLoading = isCreating || isUpdating;

  const schema: yup.ObjectSchema<ClientFormData> = yup.object({
    //companyName: yup.string().required().notLongWord(),
    companyName: yup.string().notLongWord(),
    name: yup.string().required(),
    address: yup.string().required(),
    city: yup.string().required(),
    cp: yup.string().required(),
    //phone: yup.string().notRequired().strict(),
    phone: yup.string().required().strict()
  }) as any;

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue
  } = useCustomForm<ClientFormData>({
    defaultValues: {
      companyName: '',
      name: '',
      address: '',
      city: '',
      cp: '',
      phone: '',
    },
    resolver: yupResolver(schema),
    mode: 'all',
  });

  useEffect(() => {
    if (client?.data) {
      reset({
        companyName: client.data.companyName || '',
        name: client.data.name || '',
        address: client.data.address || '',
        city: client.data.city || '',
        cp: client.data.cp || '',
        phone: client.data.phone || '',
      });
    }
  }, [client, reset]);

  const onSubmit = async (formData: ClientFormData) => {
    try {
      // const payload: any = {
      //   id: client?.id,
      //   user_id: userId, // 7530
      //   data: {
      //     companyName: formData.companyName,
      //     name: formData.name,
      //     address: formData.address,
      //     city: formData.city,
      //     cp: formData.cp,
      //     phone: formData.phone,
      //   }
      // };


      if (client?.id) {
        const payload: any = { ...client, data: formData, user_id: authState.profile.id };

        // Update existing client
        await updateClient(payload as any).unwrap();
        dispatch(addNotification({
          type: 'success',
          message: t('client_update_success'),
        }));
      } else {
        const payload: any = { ...client, data: formData, user_id: authState.profile.id };

        // Create new client
        await createClient(payload).unwrap();
        dispatch(addNotification({
          type: 'success',
          message: t('client_create_success'),
        }));
      }

      closePopup();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: t("Something went wrong"),
      }));
      console.error(error);
    }
  };

  const onChangeNumberOnly = useCallback((e: any) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, '') // NOSONAR
    setValue(e.target.name, e.target.value, {shouldValidate: true, shouldDirty: true});
  }, [setValue])

  const onChangeTextOnly = useCallback((e: any) => {
    e.target.value = e.target.value.replace(/[^A-zÀ-ú0-9 +-_']/gi, '') // NOSONAR
    setValue(e.target.name, e.target.value, {shouldValidate: true, shouldDirty: true});
  }, [setValue])

  return (
    <Popup
      title={client ? t("MODIFY_CUSTOMER_FILE") : t("ADD_CUSTOMER")}
      translateTitle
      fixSize="medium"
      commandPanelProps={{
        additionalElement: (
          <group>
            <group data-justify="start" data-gap="20" data-fit="1">
              <Button
                data-length="forcefit"
                secondary
                large
                onClick={handleSubmit(onSubmit)}
              >
                {t("NAV_SAVE")}
              </Button>
              <Button
                data-length="forcefit"
                accent
                secondary
                large
                onClick={closePopup}
              >
                {t("CANCEL")}
              </Button>

            </group>
            {client?.id && (
              <group data-justify="end" data-gap="20" data-length="forcefit" data-fit="1">
                <Button
                  data-length="forcefit"
                  simple
                  secondary
                  large
                  material={false}
                  onClick={() => {
                    openPopup(
                      <ConfirmationPopup
                        title={t("DELETE_CLIENT")}
                        message={"DELETE_WARNING"}
                        translateMessage={true}
                        icon="warning"
                        positiveCallback={() => {
                          deleteClient(client?.id as number)
                          closeLastPopup();
                        }}
                        negativeCallBack={() => {
                        }}
                      ></ConfirmationPopup>
                    )
                    // openPopup(
                    //   <DeleteClientPopup
                    //     client={client}
                    //     closePopup={closeLastPopup}    // closes DeleteClientPopup
                    //     onDeleted={closePopup}         // closes ClientPopup
                    //   ></DeleteClientPopup>
                    // );
                  }}
                >
                  <>
                    <icon data-svg-size="24" data-svg-color="secondary">
                      <SvgSupprimer />
                    </icon>
                    {t("DELETE_FILE")}
                  </>
                </Button>
              </group>
            )}
          </group>
        )
      }}
    >
      {(isLoading || isDeleting || isUpdating) && <Loader />}
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10">
          <group data-direction="column" data-gap="10" data-length="autofit">
            <text data-weight="400" data-text-size="14" data-ellipsis="" data-color="grey-color-dark">
              <i data-space-horizontal="5" data-color="red">*</i> {t("REQUIRED_FIELDS")}
            </text>
          </group>
        </group>

        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            <group data-gap="20">
              <group>
                <group data-gap="10">
                  <text data-weight="400" data-text-size="medium" data-ellipsis="" data-color="grey-color-dark">
                    {t("CUSTOMER_IDENTITY")}<i data-space-horizontal="5" data-color="red">*</i>
                  </text>
                </group>
                <group data-gap="10">
                  <text data-weight="400" data-text-size="14" data-ellipsis="" data-color="grey-color-dark">
                    {t("CUSTOMER_IDENTITY_WARNING")}
                  </text>
                </group>
              </group>


              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"CLIENT_COMPAGNY_NAME"}
                  //requiredLabel={true}
                  control={control}
                  name="companyName"
                  type="text"
                  size="large"
                  //error={errors.companyName?.message}
                  error={null}
                  onChange={onChangeTextOnly}
                  maxLength={25}
                />
              </group>
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"CLIENT_NAME"}
                  requiredLabel={true}
                  control={control}
                  name="name"
                  type="text"
                  size="large"
                  error={null}
                />
              </group>
            </group>

            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"CLIENT_ADDRESS"}
                  requiredLabel={true}
                  control={control}
                  name="address"
                  type="text"
                  size="large"
                  //error={errors.address?.message}
                  error={null}
                  onChange={onChangeTextOnly}
                  maxLength={50}
                />
              </group>
            </group>

            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"CLIENT_CP"}
                  requiredLabel={true}
                  control={control}
                  name="cp"
                  type="text"
                  size="large"
                  //error={errors.cp?.message}
                  error={null}
                  onChange={onChangeNumberOnly}
                  maxLength={10}
                />
              </group>
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"CLIENT_CITY"}
                  requiredLabel={true}
                  control={control}
                  name="city"
                  type="text"
                  size="large"
                  //error={errors.city?.message}
                  error={null}
                  onChange={onChangeTextOnly}
                  maxLength={25}
                />
              </group>
            </group>
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={"CLIENT_PHONE"}
                  requiredLabel={true}
                  control={control}
                  name="phone"
                  type="text"
                  size="large"
                  //error={errors.phone?.message}
                  error={null}
                  onChange={onChangeNumberOnly}
                  maxLength={15}
                />
              </group>
            </group>
          </group>
        </group>
      </group>
    </Popup>
  );
};